/**
 * Staying in step with the server.
 *
 * The rules, in one place:
 *   - A completed sale goes into the outbox on this device first, always.
 *   - The outbox is emptied whenever the connection is up, on a timer, and the
 *     moment the browser says it is back online.
 *   - Uploading is safe to repeat: every sale carries an id the device made, and
 *     the server ignores an id it already holds. A retry can never duplicate.
 *   - A sale the server REFUSES (a 4xx - a deleted product, an unapproved
 *     device) stops retrying and is shown to a human instead of looping forever.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from './api.js';
import { thisShop } from './shop.js';
import { db, getMeta, outboxCount, queueSale, saveSnapshot, type Snapshot } from './db.js';
import { deviceId, syncCounter } from './device.js';
import { ensurePersistentStorage, lastPersistState, type PersistState } from './storage.js';
import { pruneOffline } from './offlineLogin.js';
import { keepPhotosForOffline } from './image.js';

/** What the server says back when it stores a sale. */
export interface SaveResult {
  id: string;
  receiptNo: string;
  duplicate: boolean;
  renumbered: boolean;
  creditIssued?: { code: string; amount: number } | null;
  warnings: string[];
}

export type NetState = 'online' | 'offline' | 'syncing';

interface SyncCtx {
  state: NetState;
  pending: number;
  stuck: number;
  /** Hours the oldest unsent sale has been waiting, or null if none. */
  waitingHours: number | null;
  lastSyncAt: string | null;
  snapshotAt: string | null;
  /** Whether the browser promised to keep this device's data. null until asked. */
  storagePersisted: PersistState | null;
  /** Ask the browser again (from a click, so a browser that prompts can). */
  retryPersist: () => Promise<PersistState>;
  /** Put a completed sale in the outbox and try to send it right away. */
  submitSale: (id: string, payload: unknown) => Promise<{ queued: boolean; sent: boolean; result?: SaveResult }>;
  flush: () => Promise<void>;
  refreshCatalog: () => Promise<void>;
  stuckSales: () => Promise<{ id: string; lastError?: string | null }[]>;
}

const Ctx = createContext<SyncCtx | null>(null);

const FLUSH_EVERY_MS = 20_000;
const CATALOG_EVERY_MS = 5 * 60_000;

export function SyncProvider({ children, signedIn }: { children: ReactNode; signedIn: boolean }) {
  const [state, setState] = useState<NetState>(navigator.onLine ? 'online' : 'offline');
  const [pending, setPending] = useState(0);
  const [stuck, setStuck] = useState(0);
  const [waitingHours, setWaitingHours] = useState<number | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null);
  const [storagePersisted, setStoragePersisted] = useState<PersistState | null>(null);
  const busy = useRef(false);

  const retryPersist = useCallback(async () => {
    const r = await ensurePersistentStorage();
    setStoragePersisted(r);
    return r;
  }, []);

  /*
   * Tell the server what is still on this till. It cannot see a device's
   * storage, so this is the only way the shop owner - or the owner of the
   * system - learns that a till is holding sales it could not upload. Sent when
   * the numbers change, and at least every few minutes while signed in.
   */
  const lastReport = useRef<{ sig: string; at: number }>({ sig: '', at: 0 });
  const report = useCallback(async (pendingN: number, stuckN: number, oldest: string | undefined) => {
    if (!navigator.onLine || !signedIn) return;
    const sig = `${pendingN}|${stuckN}|${oldest ?? ''}`;
    if (sig === lastReport.current.sig && Date.now() - lastReport.current.at < 5 * 60_000) return;
    try {
      await api('/auth/till-report', { body: { pending: pendingN, stuck: stuckN, oldestPendingAt: oldest ?? null } });
      lastReport.current = { sig, at: Date.now() };
    } catch {
      /* offline or refused: the next count tries again */
    }
  }, [signedIn]);

  const recount = useCallback(async () => {
    const all = await db.outbox.toArray();
    const live = all.filter((x) => !x.stuck);
    setPending(live.length);
    setStuck(all.length - live.length);
    const oldest = live.map((x) => x.createdAt).sort()[0];
    setWaitingHours(oldest ? (Date.now() - new Date(oldest).getTime()) / 3_600_000 : null);
    void report(live.length, all.length - live.length, oldest);
  }, [report]);

  const flush = useCallback(async () => {
    if (busy.current || !navigator.onLine) return;
    const queued = await db.outbox.filter((x) => !x.stuck).toArray();
    if (queued.length === 0) {
      setState(navigator.onLine ? 'online' : 'offline');
      return;
    }

    busy.current = true;
    setState('syncing');
    try {
      const res = await api<{ results: { id: string; ok: boolean; retry?: boolean; message?: string }[] }>(
        '/sales/sync',
        { body: { sales: queued.map((q) => q.payload) }, timeoutMs: 40_000 },
      );

      for (const r of res.results) {
        if (r.ok) {
          await db.outbox.delete(r.id);
        } else if (r.retry === false) {
          // The server will never accept this. Stop looping; a person must look.
          await db.outbox.update(r.id, { stuck: true, lastError: r.message ?? 'Refused' });
        } else {
          const row = await db.outbox.get(r.id);
          await db.outbox.update(r.id, { attempts: (row?.attempts ?? 0) + 1, lastError: r.message });
        }
      }
      setLastSyncAt(new Date().toISOString());
      setState('online');
    } catch (e) {
      // No connection, or the server is down. Everything stays in the outbox.
      setState(navigator.onLine && !(e instanceof ApiError && e.isOffline) ? 'online' : 'offline');
    } finally {
      busy.current = false;
      await recount();
    }
  }, [recount]);

  const refreshCatalog = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      // The shop is in the address only for the service worker, which keeps its
      // offline copy by address: without it two shops would share one copy.
      const snap = await api<Snapshot>(`/catalog/snapshot?shop=${encodeURIComponent(thisShop()?.storageKey ?? '')}`, {
        timeoutMs: 60_000,
      });
      await saveSnapshot(snap);
      setSnapshotAt(snap.generatedAt);
      void keepPhotosForOffline(snap.items.map((i) => i.imageUrl));
      // Staff the owner removed lose their offline sign-in on this device too.
      await pruneOffline(snap.users.map((u) => String(u.username ?? ''))).catch(() => undefined);
    } catch {
      // Keep whatever is already cached; the till must never be blocked by this.
    }
  }, []);

  const submitSale = useCallback(
    async (id: string, payload: unknown) => {
      // On the device first. Always. Before any network call.
      await queueSale(id, payload);
      await recount();

      if (!navigator.onLine) return { queued: true, sent: false };
      try {
        const result = await api<SaveResult>('/sales', { body: payload, timeoutMs: 12_000 });
        await db.outbox.delete(id);
        await recount();
        return { queued: true, sent: true, result };
      } catch (e) {
        if (e instanceof ApiError && !e.isOffline && e.status < 500) {
          await db.outbox.update(id, { stuck: true, lastError: e.message });
          await recount();
        }
        return { queued: true, sent: false };
      }
    },
    [recount],
  );

  const stuckSales = useCallback(async () => {
    const rows = await db.outbox.filter((x) => !!x.stuck).toArray();
    return rows.map((r) => ({ id: r.id, lastError: r.lastError }));
  }, []);

  // Browser online/offline events, a timer, and a refresh when the tab returns.
  useEffect(() => {
    if (!signedIn) return;
    const onOnline = () => {
      setState('online');
      void flush();
    };
    const onOffline = () => setState('offline');
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flush();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);

    void recount();
    void flush();
    void refreshCatalog();
    void getMeta<{ generatedAt: string }>('snapshot').then((s) => setSnapshotAt(s?.generatedAt ?? null));
    // Ask the browser to keep our data, and remember what it said.
    void lastPersistState().then((prev) => setStoragePersisted((cur) => cur ?? prev));
    void retryPersist();

    const t1 = setInterval(() => {
      void flush();
      void recount();
    }, FLUSH_EVERY_MS);
    const t2 = setInterval(() => void refreshCatalog(), CATALOG_EVERY_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [signedIn, flush, refreshCatalog, recount, retryPersist]);

  // Keep this device's receipt counter at least as high as the server's.
  useEffect(() => {
    if (!signedIn) return;
    void (async () => {
      try {
        const d = await api<{ lastCounter: number; seriesPrefix: string }>(`/auth/device/${deviceId()}`, {
          auth: false,
        });
        syncCounter(Number(d.lastCounter ?? 0));
      } catch {
        /* offline - the local counter is already correct */
      }
    })();
  }, [signedIn]);

  const value = useMemo<SyncCtx>(
    () => ({
      state, pending, stuck, waitingHours, lastSyncAt, snapshotAt, storagePersisted,
      submitSale, flush, refreshCatalog, stuckSales, retryPersist,
    }),
    [state, pending, stuck, waitingHours, lastSyncAt, snapshotAt, storagePersisted, submitSale, flush, refreshCatalog, stuckSales, retryPersist],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSync(): SyncCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSync used outside the provider');
  return c;
}

export async function outboxSize(): Promise<number> {
  return outboxCount();
}
